import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { useStoreBase } from "@/lib/store";
import { toast } from "sonner";

const COLORS = ["#E8A317", "#C47B2B", "#8B9A46", "#6B8F71", "#D97706", "#A16207"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function StorefrontAbpMix() {
  const { mixSlug } = useParams();
  const { slug, base } = useStoreBase();
  const nav = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [err, setErr] = useState("");
  const [qty, setQty] = useState({});
  const [picked, setPicked] = useState({});
  const [mixName, setMixName] = useState("");

  useEffect(() => {
    setErr("");
    api.get(`/store/${slug}/abp/${mixSlug}`)
      .then(({ data }) => {
        setBundle(data);
        setQty({});
        setPicked({});
      })
      .catch((e) => setErr(extractErr(e)));
  }, [slug, mixSlug]);

  const used = useMemo(() => Object.values(qty).reduce((s, n) => s + Number(n || 0), 0), [qty]);
  const productsTotal = useMemo(() => {
    if (!bundle) return 0;
    return (bundle.products || []).reduce((s, p) => s + Number(p.price || 0) * Number(qty[p.id] || 0), 0);
  }, [bundle, qty]);
  const services = useMemo(() => {
    if (!bundle || used < 1) return [];
    return (bundle.services || []).filter((s) => picked[s.id || s.name]).map((s) => ({
      id: s.id || s.name,
      name: s.name,
      per_kg: Number(s.per_kg),
      kg: used,
      amount: Number(s.per_kg) * used,
    }));
  }, [bundle, qty, used, picked]);
  const servicesTotal = services.reduce((s, r) => s + r.amount, 0);
  const total = productsTotal + servicesTotal;

  if (err) return <div className="max-w-3xl mx-auto p-8 text-stone-600" data-testid="abp-mix-error">{err}</div>;
  if (!bundle) return <div className="max-w-3xl mx-auto p-8 text-stone-500">Loading mixture…</div>;

  const setProductQty = (p, next) => {
    const stock = p.available_kg == null ? 999 : Number(p.available_kg);
    const capped = Math.max(0, Math.min(next, stock));
    setQty((prev) => ({ ...prev, [p.id]: capped }));
  };

  const confirm = () => {
    if (used < 1) {
      toast.error("Add at least 1 KG");
      return;
    }
    const items = Object.entries(qty).filter(([, q]) => Number(q) > 0).map(([product_id, q]) => ({ product_id, qty: Number(q) }));
    const service_ids = (bundle.services || []).filter((s) => picked[s.id || s.name]).map((s) => s.id || s.name);
    nav(`${base}/checkout`, {
      state: {
        abpMix: {
          bundle_id: bundle.id,
          name: bundle.title,
          target_kg: used,
          mix_name: mixName.trim(),
          items,
          service_ids,
          price: total,
        },
      },
    });
  };

  const filled = (bundle.products || []).filter((p) => Number(qty[p.id] || 0) > 0);

  return (
    <div className="w-full min-w-0 max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-10 overflow-x-hidden" data-testid="abp-mix">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 sm:gap-6 items-start min-w-0">
        <div className="min-w-0">
          <div className="flex gap-3 sm:gap-4 items-start mb-4 sm:mb-5 min-w-0">
            <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-stone-100 shrink-0">
              {bundle.image ? <img src={mediaUrl(bundle.image)} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-extrabold text-stone-900 break-words" data-testid="abp-mix-title">{bundle.title}</h1>
              {bundle.description ? <p className="mt-1 text-sm text-stone-600">{bundle.description}</p> : null}
              <p className="mt-2 text-xs text-stone-500">1 quantity = 1 KG · add as much as is available</p>
            </div>
          </div>
          <div className="divide-y divide-stone-200 border border-stone-200 rounded-2xl bg-white min-w-0">
            {(bundle.products || []).map((p) => {
              const q = Number(qty[p.id] || 0);
              return (
                <div key={p.id} className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-4 min-w-0" data-testid={`abp-product-${p.id}`}>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl overflow-hidden bg-stone-100 shrink-0">
                    {p.image ? <img src={mediaUrl(p.image)} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm sm:text-base text-stone-900 break-words leading-tight">{p.name}</div>
                    <div className="text-[11px] sm:text-xs text-stone-500 break-words">{inr(p.price)}/KG{p.available_kg != null ? ` · ${p.available_kg} KG` : ""}{q ? ` · ${inr(p.price * q)}` : ""}</div>
                  </div>
                  <div className="inline-flex items-center rounded-xl border border-stone-200 bg-stone-50 shrink-0">
                    <button type="button" className="w-8 h-8 sm:w-9 sm:h-9 font-bold" aria-label={`Decrease ${p.name}`} data-testid={`abp-minus-${p.id}`} onClick={() => setProductQty(p, q - 1)}>−</button>
                    <span className="w-5 sm:w-6 text-center text-sm font-bold" data-testid={`abp-qty-${p.id}`}>{q}</span>
                    <button type="button" className="w-8 h-8 sm:w-9 sm:h-9 font-bold" aria-label={`Increase ${p.name}`} data-testid={`abp-plus-${p.id}`} onClick={() => setProductQty(p, q + 1)}>+</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <aside className="w-full min-w-0 rounded-2xl bg-emerald-800 text-white p-4 sm:p-5 lg:sticky lg:top-24" data-testid="abp-summary">
          <div className="text-xl sm:text-3xl font-extrabold break-words" data-testid="abp-target-kg">Aapka mix {used} KG</div>
          <div className="mt-4 h-3 rounded-full overflow-hidden bg-emerald-950/40 flex" data-testid="abp-bar">
            {used > 0 && filled.map((p, i) => (
              <div key={p.id} style={{ width: `${(Number(qty[p.id]) / used) * 100}%`, background: COLORS[i % COLORS.length] }} />
            ))}
          </div>
          <ul className="mt-4 space-y-1.5 text-sm">
            {filled.map((p, i) => (
              <li key={p.id} className="flex justify-between gap-2 min-w-0">
                <span className="flex items-center gap-2 min-w-0"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: COLORS[i % COLORS.length] }} /><span className="truncate">{p.name}</span></span>
                <span className="shrink-0">{qty[p.id]} KG · {Math.round((Number(qty[p.id]) / used) * 100)}%</span>
              </li>
            ))}
            {used < 1 ? <li className="text-emerald-100">Add products to build the mix</li> : null}
          </ul>
          {(bundle.services || []).length > 0 ? (
            <div className="mt-4 space-y-2" data-testid="abp-service-picks">
              {(bundle.services || []).map((s) => {
                const id = s.id || s.name;
                const on = Boolean(picked[id]);
                return (
                  <label key={id} className="flex items-start gap-2 text-sm cursor-pointer min-w-0">
                    <input type="checkbox" className="mt-0.5 shrink-0" checked={on} data-testid={`abp-service-${id}`} onChange={() => setPicked((prev) => ({ ...prev, [id]: !prev[id] }))} />
                    <span className="flex-1 min-w-0 break-words">{s.name}</span>
                    <span className="shrink-0 text-right">₹{s.per_kg}/KG{on && used > 0 ? ` · ${inr(Number(s.per_kg) * used)}` : ""}</span>
                  </label>
                );
              })}
            </div>
          ) : null}
          <div className="mt-4 space-y-1 text-sm border-t border-white/15 pt-3">
            <div className="flex justify-between"><span>Chune hue products</span><span>{inr(productsTotal)}</span></div>
            {services.map((s) => (
              <div key={`${s.name}-${s.kg}`} className="flex justify-between gap-3" data-testid="abp-service-line">
                <span>{s.name} · {s.kg} KG × ₹{s.per_kg}</span>
                <span>{inr(s.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between text-lg font-extrabold pt-1"><span>Total</span><span data-testid="abp-total">{inr(total)}</span></div>
          </div>
          <label className="block mt-4 text-xs text-emerald-100">
            Recipe ka naam
            <input value={mixName} onChange={(e) => setMixName(e.target.value)} className="mt-1 w-full h-10 rounded-lg px-3 text-stone-900" data-testid="abp-mix-name" placeholder="Mera mix" />
          </label>
          <button type="button" className="mt-3 h-11 w-full rounded-xl bg-emerald-950 font-bold" data-testid="abp-confirm" onClick={confirm}>Mix confirm karein</button>
        </aside>
      </div>
    </div>
  );
}
